/**
 * tm/useFileDrop.ts — 파일 끌어다 놓기 공용 훅 (2026-10-29 지도사 「PDF 또는 음원 파일 올릴 때 드래그 앤 드롭 되게」)
 *  - 상담 메모 PDF(ReportMemoBar) · 동행상담 녹음(ConsultRecordings) · 통화 녹음(CallRecordings)이 같이 쓴다
 *  - 파일을 끌 때만 반응(글자 · 링크 끌기는 무시) · 안쪽 칸을 지날 때 깜박이지 않게 들어간 깊이를 센다
 *  - 칸을 살짝 벗어나 놓아도 브라우저가 파일을 열어 버려 입력 중이던 창이 날아가지 않게, 이 훅이 화면에 있는 동안 창 전체의 「파일 열기」를 막는다
 */
import { useEffect, useRef, useState } from 'react';
import type { DragEvent as RDragEvent } from 'react';

let guards = 0;
const block = (e: DragEvent) => {
  if (e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files')) e.preventDefault();
};
function addGuard() {
  if (guards++ === 0) { window.addEventListener('dragover', block); window.addEventListener('drop', block); }
}
function removeGuard() {
  if (--guards === 0) { window.removeEventListener('dragover', block); window.removeEventListener('drop', block); }
}

const hasFiles = (e: RDragEvent<HTMLElement>) => Array.from(e.dataTransfer.types || []).includes('Files');

export interface FileDrop {
  /** 파일을 칸 위에 끌고 있는 중 */
  over: boolean;
  /** 놓을 칸에 펼쳐 넣는 이벤트 */
  bind: {
    onDragEnter: (e: RDragEvent<HTMLElement>) => void;
    onDragOver: (e: RDragEvent<HTMLElement>) => void;
    onDragLeave: (e: RDragEvent<HTMLElement>) => void;
    onDrop: (e: RDragEvent<HTMLElement>) => void;
  };
}

/** onFile — 놓은 파일 중 첫 번째 · disabled 면(올리는 중 등) 놓아도 아무 일 없음 */
export function useFileDrop(onFile: (f: File) => void, disabled = false): FileDrop {
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  useEffect(() => { addGuard(); return removeGuard; }, []);
  useEffect(() => { if (disabled) { depth.current = 0; setOver(false); } }, [disabled]);
  return {
    over,
    bind: {
      onDragEnter: e => { if (!hasFiles(e) || disabled) return; e.preventDefault(); depth.current += 1; setOver(true); },
      onDragOver: e => { if (!hasFiles(e) || disabled) return; e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; },
      onDragLeave: e => { if (!hasFiles(e)) return; depth.current = Math.max(0, depth.current - 1); if (depth.current === 0) setOver(false); },
      onDrop: e => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        depth.current = 0; setOver(false);
        if (disabled) return;
        const f = e.dataTransfer.files && e.dataTransfer.files[0];
        if (f) onFile(f);
      },
    },
  };
}
